<!-- Full-screen overlay -->
<div class="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100">

  <!-- Header bar -->
  <header class="flex items-center gap-4 px-6 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
    <button (click)="close.emit()"
      class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>

    <div class="flex-1 min-w-0">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white truncate">Review: {{ fileName() }}</h2>
      @if (versionId) {
        <p class="text-xs text-gray-400 font-mono truncate">{{ versionId }}</p>
      }
    </div>

    @if (totalPages > 0) {
      <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
        {{ totalPages }} page{{ totalPages !== 1 ? 's' : '' }}
      </span>
    }

    @if (saveError) {
      <span class="text-xs text-red-500 flex-shrink-0">{{ saveError }}</span>
    }

    <button
      (click)="saveApproved()"
      [disabled]="saving || loading"
      class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0">
      @if (saving) {
        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Saving…
      } @else {
        Save as Approved
      }
    </button>
  </header>

  <!-- Loading / error state -->
  @if (loading) {
    <div class="flex-1 flex items-center justify-center text-gray-400 text-sm">
      Loading document…
    </div>
  } @else if (loadError) {
    <div class="flex-1 flex items-center justify-center text-red-500 text-sm">
      {{ loadError }}
    </div>
  } @else {

    <!-- Main split panel -->
    <div class="flex flex-1 min-h-0">

      <!-- Left: Markdown editor / preview (55%) -->
      <div class="flex flex-col w-[55%] border-r border-gray-200 dark:border-gray-800 min-w-0">
        <div class="flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
          <span class="text-xs font-medium text-gray-500 dark:text-gray-400 flex-1">
            {{ previewMode ? 'Preview' : 'Markdown' }}
          </span>
          <button
            (click)="togglePreview()"
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
            [class.bg-gray-200]="!previewMode"
            [class.dark:bg-gray-700]="!previewMode"
            [class.text-gray-700]="!previewMode"
            [class.dark:text-gray-200]="!previewMode"
            [class.bg-blue-100]="previewMode"
            [class.dark:bg-blue-900]="previewMode"
            [class.text-blue-700]="previewMode"
            [class.dark:text-blue-300]="previewMode"
            aria-label="Toggle preview">
            @if (previewMode) {
              <!-- Eye-off: back to edit -->
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Edit
            } @else {
              <!-- Eye: switch to preview -->
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
              Preview
            }
          </button>
        </div>

        @if (previewMode) {
          <div
            [innerHTML]="markedHtml"
            class="flex-1 overflow-auto p-4 prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100">
          </div>
        } @else {
          <textarea
            [(ngModel)]="markdownContent"
            class="flex-1 w-full resize-none font-mono text-sm p-4 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none leading-relaxed">
          </textarea>
        }
      </div>

      <!-- Right: Page viewer (45%) -->
      <div class="flex flex-col w-[45%] min-w-0">

        <!-- Page navigation bar -->
        <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
          <button (click)="prevPage()" [disabled]="currentPage <= 1"
            class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <span class="text-xs text-gray-600 dark:text-gray-300 flex-shrink-0">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button (click)="nextPage()" [disabled]="currentPage >= totalPages"
            class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <div class="flex-1"></div>
          <span class="text-[10px] text-gray-400 dark:text-gray-500">Red boxes = low confidence</span>
        </div>

        <!-- Main page image -->
        <div class="flex-1 overflow-auto flex items-start justify-center p-4 bg-gray-100 dark:bg-gray-800 min-h-0">
          @if (currentPageUrl) {
            <img
              [src]="currentPageUrl"
              [alt]="'Page ' + currentPage"
              class="max-w-full shadow-lg rounded"
              draggable="false">
          } @else {
            <div class="flex items-center justify-center h-full text-sm text-gray-400">
              No image for this page
            </div>
          }
        </div>

        <!-- Thumbnail strip -->
        @if (totalPages > 1) {
          <div class="flex gap-2 px-3 py-2 overflow-x-auto border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
            @for (page of thumbnailPages(); track page.pageNum) {
              <button
                (click)="goToPage(page.pageNum)"
                class="flex-shrink-0 relative rounded overflow-hidden border-2 transition-colors"
                [class.border-blue-500]="currentPage === page.pageNum"
                [class.border-transparent]="currentPage !== page.pageNum">
                <img
                  [src]="page.url"
                  [alt]="'Page ' + page.pageNum"
                  class="h-16 w-auto object-contain bg-white"
                  draggable="false">
                <span class="absolute bottom-0 left-0 right-0 text-center text-[9px] text-white bg-black/50 leading-4">
                  {{ page.pageNum }}
                </span>
              </button>
            }
            @if (totalPages > 20) {
              <span class="flex-shrink-0 self-center text-xs text-gray-400 px-1">
                +{{ totalPages - 20 }} more
              </span>
            }
          </div>
        }

      </div>
    </div>
  }

</div>
